// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import Button from "@adl/MobiusOverrides/Button/Button";
import { EMPTY_VALUE } from "@adl/XDL/constants";
import { color, getDataLayer, xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Xng-Changes
const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);

    return {
        acceptBidEvent: state.xGA.acceptBid,
        acceptBidEventName: state.xGA.acceptBidEventName,
        affiliation: getBillToState(state, session.billToId).value?.companyName,
    };
};
interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes
    showActions: boolean;
    acceptQuoteClickHandler: (id: string) => any;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    dataCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    acceptBidButton?: ButtonPresentationProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
    customerCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    shipToCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 26px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
    acceptBidButton: {
        variant: "primary",
        sizeVariant: "small",
    },
};

const QuoteSummaryCard: FC<Props> = ({
    quote,
    showActions,
    session,
    quoteSettings,
    extendedStyles,
    acceptQuoteClickHandler,
    acceptBidEvent,
    acceptBidEventName,
    affiliation,
}) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    const acceptQuote = (quote: QuoteModel) => {
        if(acceptBidEvent) {
            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            const data = {
                event: acceptBidEventName,
                affiliation,
                user: quote.userName,
                home_branch: session.pickUpWarehouse?.name,
                sales_rep: quote.salespersonName,
                order_number: quote.orderNumber,
                order_amount: quote.orderSubTotal,
            };
            
            dataLayer.push(data);

        }
        acceptQuoteClickHandler(quote.id);
    }

    // XNG-Changes: change structure to Table , remove Customer , remove Ship To , add Amount , Add Action
    return (
        <>
            <DataTableCell data-th={translate("Quote #")} {...styles.dataCell}>
                <RfqQuoteDetailsPageTypeLink
                    title={!quote.erpOrderNumber ? translate("Pending") : quote.erpOrderNumber}
                    quoteId={quote.id}
                />
            </DataTableCell>
            {quoteSettings.jobQuoteEnabled && (
                <DataTableCell data-th={translate("َType")} {...styles.dataCell}>
                    {quote.typeDisplay}
                </DataTableCell>
            )}
            <DataTableCell data-th={translate("Status")} {...styles.dataCell}>
                {quote.statusDisplay}
            </DataTableCell>
            <DataTableCell data-th={translate("Po #")} {...styles.dataCell}>
                {quote.poNumber}
            </DataTableCell>
            <DataTableCell data-th={translate("Job Name")} {...styles.dataCell}>
                {quote.jobName}
            </DataTableCell>
            <DataTableCell data-th={translate("Requested")} {...styles.dataCell}>
                {orderDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Expires")} {...styles.dataCell}>
                {expirationDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            {session.isSalesPerson && (
                <>
                    <DataTableCell data-th={translate("Sales Rep")} {...styles.dataCell}>
                        {quote.salespersonName || EMPTY_VALUE}
                    </DataTableCell>
                    <DataTableCell data-th={translate("User")} {...styles.dataCell}>
                        {quote.userName || EMPTY_VALUE}
                    </DataTableCell>
                </>
            )}
            {/* {customerDisplay && (
                <DataTableCell data-th={translate("Customer")} {...styles.customerCell}>
                    {customerDisplay || EMPTY_VALUE}
                </DataTableCell>
            )} */}
            {/* {!session.isSalesPerson && (
                <DataTableCell data-th={translate("Ship To / Pick Up")} {...styles.shipToCell}>
                    {quote.shipToFullAddress || EMPTY_VALUE}
                </DataTableCell>
            )} */}
            <DataTableCell {...styles.shipToCell}>
                {quote.status !== "Submitted" && quote.orderSubTotalDisplay}
            </DataTableCell>
            {showActions && (
                <DataTableCell {...styles.shipToCell}>
                    {quote.status === "QuoteProposed" && (
                        <Button {...styles.acceptBidButton} onClick={() => acceptQuote(quote)}>
                            {translate("Accept Bid")}
                        </Button>
                    )}
                </DataTableCell>
            )}
        </>
    );
};

export default connect(mapStateToProps)(QuoteSummaryCard);
